import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useGetCustomerDropdownQuery } from '../../api/customerApi';
import { useLanguage } from '../../contexts/LanguageContext';

const CustomerDropdown = ({ value, onChange, className, disabled, placeholder, newCustomer = null, chassisNo }) => {
    const { th } = useLanguage();
    const resolvedPlaceholder = placeholder || th('common.selectCustomer');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filterValue, setFilterValue] = useState('');
    const prevValueRef = useRef(value);
    const debounceTimerRef = useRef(null);

    const { data: customerList = [], isLoading: isLoadingCustomers } = useGetCustomerDropdownQuery({
        Name: '',
        Surname: '',
        SearchText: searchText,
        PageSize: 50,
        PageNumber: 1,
        ChassisNo: chassisNo ? String(chassisNo).trim() : ''
    });

    let customerOptions = customerList.map((customer) => ({
        id: customer.id,
        name: customer.name,
        surname: customer.surname,
        phoneNumber: customer.phoneNumber,
        displayValue: `${customer.name} ${customer.surname}`,
        fullData: customer,
        licensePlate: customer.licensePlate ?? ''
    }));

    if (newCustomer && !customerOptions.find((option) => option.id === newCustomer.id)) {
        const newCustomerOption = {
            id: newCustomer.id,
            name: newCustomer.name,
            surname: newCustomer.surname,
            phoneNumber: newCustomer.phoneNumber,
            displayValue: `${newCustomer.name} ${newCustomer.surname}`,
            fullData: newCustomer,
            licensePlate: newCustomer.licensePlate ?? ''
        };
        customerOptions = [newCustomerOption, ...customerOptions];
    }

    const handleSelectionChange = (e) => {
        const selected = e.value;
        setSelectedCustomer(selected);

        setFilterValue('');
        setSearchText('');

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        onChange(selected ? selected.id : null, selected ? selected.fullData : null);
    };

    // useCallback ile memoize ediyoruz - her render'da yeni fonksiyon oluşturulmasını önlüyoruz
    const handleFilterInputChange = useCallback((value) => {
        setFilterValue(value);

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            setSearchText(value);
        }, 500);
    }, []);

    // useCallback ile memoize ediyoruz - Bu kritik!
    // Her render'da yeni fonksiyon oluşturulursa PrimeReact filter input'unu resetliyor
    const filterTemplate = useCallback(
        () => (
            <div className="p-dropdown-filter-container">
                <InputText value={filterValue} onChange={(e) => handleFilterInputChange(e.target.value)} className="p-dropdown-filter w-full" placeholder={th('common.search')} autoFocus type="text" inputMode="text" />
                <span className="p-dropdown-filter-icon pi pi-search" />
            </div>
        ),
        [filterValue, th, handleFilterInputChange]
    );

    // Cleanup
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const valueChanged = prevValueRef.current !== value;
        prevValueRef.current = value;

        if (selectedCustomer) {
            if (typeof value === 'number' && selectedCustomer.id === value) {
                return;
            } else if (typeof value === 'object' && value !== null && selectedCustomer.id === value.id) {
                return;
            }
        }

        if (customerOptions.length > 0 && value) {
            let found;

            if (typeof value === 'number') {
                found = customerOptions.find((option) => option.id === value);
            } else if (typeof value === 'object' && value !== null) {
                found = customerOptions.find((option) => option.id === value.id);
            }

            if (found && !selectedCustomer) {
                setSelectedCustomer(found);
            }
        } else if (!value && selectedCustomer) {
            setSelectedCustomer(null);
            setSearchText('');
            setFilterValue('');
        }
    }, [value, customerOptions, selectedCustomer]);

    useEffect(() => {
        if (!chassisNo) return;
        if (!selectedCustomer) return;
        const stillExists = customerOptions.some((o) => o.id === selectedCustomer.id);
        if (!stillExists) {
            setSelectedCustomer(null);
            onChange(null, null);
        }
    }, [chassisNo, customerOptions, selectedCustomer]);

    const dropdownStyles = {
        textOverflow: {
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '250px'
        },
        itemContainer: {
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '400px',
            fontWeight: 'bold'
        }
    };

    const valueTemplate = (option, props) => {
        if (option) {
            return (
                <div style={dropdownStyles.textOverflow} title={option.displayValue}>
                    {option.displayValue}
                </div>
            );
        }
        return <span>{props.placeholder}</span>;
    };

    const itemTemplate = (option) => (
        <div style={dropdownStyles.itemContainer} title={option.displayValue}>
            {option.displayValue}
        </div>
    );

    return (
        <Dropdown
            value={selectedCustomer}
            options={customerOptions}
            onChange={handleSelectionChange}
            optionLabel="displayValue"
            filter
            filterTemplate={filterTemplate}
            showClear
            className={className}
            placeholder={resolvedPlaceholder}
            loading={isLoadingCustomers}
            disabled={disabled}
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            panelStyle={{ maxWidth: '500px' }}
        />
    );
};

export default CustomerDropdown;